/**
 * permitLimitWidth.ts — the shared Permit Limit column width rule (RTCFR "Dynamic
 * Permit Limit Column Width", 2026-09-27). Kept out of permitLimitCell.tsx so that
 * component file exports only components (react-refresh).
 */
import { formatLimitsInline, type PermitLimit } from "./permitLimit";
import {
  autoFitWidth, boldFont, AUTOFIT_CELL_CHROME, AUTOFIT_HEADER_CHROME,
} from "../../components/AppDataGrid/autoFitWidth";

/**
 * The ONE Permit Limit column width rule (RTCFR "Dynamic Permit Limit Column Width",
 * 2026-09-27) — every grid with a Permit Limit column auto-fits it to its longest
 * visible limit line (or its header), between these bounds. Past `max` the lines
 * wrap and each keeps its full-text tooltip. Never set a per-form width instead.
 */
export const PERMIT_LIMIT_WIDTH = { min: 110, max: 480 } as const;

/** The text a Permit Limit cell renders — every limit on ONE line (or the scalar
 *  fallback) — i.e. exactly what `renderPermitLimitCell` draws, for width measuring. */
export function permitLimitLines(limits: PermitLimit[] | undefined, fallback: string): string[] {
  if (limits && limits.length > 0) return [formatLimitsInline(limits)];
  return fallback ? [fallback] : [];
}

/** The Permit Limit column width for a hand-rolled grid (the SDMS batch-results
 *  table) — the SAME calculator + bounds + chrome as `<AppDataGrid>` autoFit, so the
 *  column sizes identically on every form. */
export function permitLimitColumnWidth(lines: readonly string[], font: string, header = "PermitLimit"): number {
  return autoFitWidth({
    header,
    lines,
    font,
    headerFont: boldFont(font),
    min: PERMIT_LIMIT_WIDTH.min,
    max: PERMIT_LIMIT_WIDTH.max,
    cellChrome: AUTOFIT_CELL_CHROME,
    headerChrome: AUTOFIT_HEADER_CHROME,
  });
}

/** The shared `<AppDataGrid>` `autoFit` option for a Permit Limit column. */
export function permitLimitAutoFit<T>(
  limitsOf: (row: T) => PermitLimit[] | undefined,
  fallbackOf: (row: T) => string,
): { min: number; max: number; lines: (row: T) => string[] } {
  return {
    min: PERMIT_LIMIT_WIDTH.min,
    max: PERMIT_LIMIT_WIDTH.max,
    lines: (row: T) => permitLimitLines(limitsOf(row), fallbackOf(row)),
  };
}
